/**
 * B2B Store Locator Map - Mapbox GL JS
 * Uses GeoJSON source with clustering for performance.
 * Syncs list item clicks to map pan/zoom with bidirectional sync.
 */
(function() {
  'use strict';

  var MAPBOX_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.0.0/mapbox-gl.js';
  var MAPBOX_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.0.0/mapbox-gl.css';
  var FETCH_TIMEOUT = 15000; // 15 second timeout for fetches

  function loadScript(src) {
    return new Promise(function(resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve();
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = function() { reject(new Error('Failed to load script: ' + src)); };
      document.head.appendChild(s);
    });
  }

  function loadStylesheet(href) {
    return new Promise(function(resolve, reject) {
      if (document.querySelector('link[href="' + href + '"]')) {
        resolve();
        return;
      }
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = function() { reject(new Error('Failed to load stylesheet: ' + href)); };
      document.head.appendChild(link);
    });
  }

  function fetchWithTimeout(url, options, timeout) {
    return new Promise(function(resolve, reject) {
      var timer = setTimeout(function() {
        reject(new Error('Request timed out: ' + url));
      }, timeout);
      fetch(url, options).then(function(response) {
        clearTimeout(timer);
        resolve(response);
      }).catch(function(err) {
        clearTimeout(timer);
        resolve(response);
      });
    });
  }

  function fetchLocationsPage(url, pageNum, pageParam) {
    var param = pageParam || 'page';
    var pageUrl = url + (url.indexOf('?') >= 0 ? '&' : '?') + param + '=' + pageNum;
    return fetch(pageUrl, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to load locations: ' + res.status);
        return res.text().then(function(text) {
          var trimmed = text.trim();
          if (!trimmed) {
            throw new Error('Empty response from locations endpoint.');
          }
          if (trimmed.charAt(0) === '<') {
            console.error('B2B Map: Response is HTML, not JSON. First 200 chars:', trimmed.substring(0, 200));
            throw new Error('Locations endpoint returned HTML. Ensure the page template is set to "map-locations".');
          }
          try {
            return JSON.parse(trimmed);
          } catch (e) {
            console.error('B2B Map: JSON parse failed. First 500 chars:', trimmed.substring(0, 500));
            throw new Error('Invalid JSON from locations endpoint. Check page template assignment.');
          }
        });
      });
  }

  var initializedContainers = {};
  
  function initMap(config) {
    if (!config || !config.token || !config.containerId) return;

    var container = document.getElementById(config.containerId);
    if (!container) return;
    
    if (initializedContainers[config.containerId]) {
      console.warn('B2B Map: Container already initialized, skipping');
      return;
    }
    initializedContainers[config.containerId] = true;

    var locationsUrl = config.locationsUrl;
    var listEl = config.listId ? document.getElementById(config.listId) : null;
    var mapContainer = container;

    var dataPromise;
    if (locationsUrl) {
      if (listEl) listEl.innerHTML = '<p class="b2b-map-loading">Loading locations…</p>';
      if (mapContainer) mapContainer.classList.add('b2b-map-container--loading');
      dataPromise = fetchLocationsPage(locationsUrl, 1, 'page').then(function(firstPage) {
        return {
          geojson: {
            type: 'FeatureCollection',
            features: (firstPage.features && firstPage.features.length) ? firstPage.features.slice() : []
          },
          hasMorePages: firstPage.paginate && firstPage.paginate.has_next,
          pageParam: (firstPage.paginate && firstPage.paginate.page_param) || 'page',
          paginate: firstPage.paginate
        };
      });
    } else {
      dataPromise = Promise.resolve({
        geojson: config.geojson || { type: 'FeatureCollection', features: [] },
        hasMorePages: false,
        pageParam: 'page',
        paginate: null
      });
    }

    function clearLoading() {
      if (mapContainer) mapContainer.classList.remove('b2b-map-container--loading');
    }

    function showError(message) {
      clearLoading();
      console.error('B2B Map:', message);
      if (listEl) listEl.innerHTML = '<p class="b2b-map-loading b2b-map-error">' + message + '</p>';
    }

    var loadMapbox = window.mapboxgl 
      ? Promise.resolve() 
      : loadStylesheet(MAPBOX_CSS).then(function() { return loadScript(MAPBOX_JS); });

    loadMapbox.then(function() {
      return dataPromise;
    }).then(function(data) {
      var geojson = data.geojson;
      var hasMorePages = data.hasMorePages;
      var pageParam = data.pageParam;

      clearLoading();
      if (!geojson.features.length) {
        if (listEl) listEl.innerHTML = '<p class="b2b-map-loading">No locations found.</p>';
      }

      var mapboxgl = window.mapboxgl;
      if (!mapboxgl) {
        showError('Mapbox library failed to load. Please refresh.');
        return;
      }

      mapboxgl.accessToken = config.token;
      
      var bounds = null;
      if (geojson.features && geojson.features.length > 0) {
        bounds = new mapboxgl.LngLatBounds();
        geojson.features.forEach(function(feature) {
          if (feature.geometry && feature.geometry.coordinates) {
            bounds.extend(feature.geometry.coordinates);
          }
        });
      }

      var map = new mapboxgl.Map({
        container: config.containerId,
        style: 'mapbox://styles/mapbox/' + (config.style || 'light-v11'),
        center: config.center || [-98.5, 39.8],
        zoom: config.zoom || 4
      });

      map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      map.on('error', function(e) {
        console.error('Mapbox error:', e.error);
      });

      var popup = new mapboxgl.Popup({ 
        closeButton: true, 
        closeOnClick: true,
        maxWidth: '300px'
      });

      map.on('load', function() {
        try {
        function rebuildSidebar() {
          if (!config.listId || !geojson.features.length) return;
          var listEl = document.getElementById(config.listId);
          if (!listEl) return;
          var sorted = geojson.features.slice().sort(function(a, b) {
            var nameA = (a.properties && a.properties.name) || '';
            var nameB = (b.properties && b.properties.name) || '';
            return nameA.localeCompare(nameB);
          });
          var fragment = document.createDocumentFragment();
          sorted.forEach(function(feature, idx) {
            var p = feature.properties || {};
            var coords = feature.geometry && feature.geometry.coordinates;
            if (!coords || coords.length < 2) return;
            var lng = coords[0];
            var lat = coords[1];
            var article = document.createElement('article');
            article.className = 'b2b-map-list-item';
            article.setAttribute('data-lat', lat);
            article.setAttribute('data-lng', lng);
            article.setAttribute('data-index', idx);
            var addrParts = [];
            if (p.address) addrParts.push(escapeHtml(p.address));
            if (p.city || p.province || p.zip) {
              var line2 = [p.city, p.province, p.zip].filter(Boolean).join(', ');
              if (line2) addrParts.push('<br>' + escapeHtml(line2));
            }
            if (p.country) addrParts.push('<br>' + escapeHtml(p.country));
            var addrHtml = addrParts.join('');
            article.innerHTML =
              '<h3 class="b2b-map-list-item__name">' + escapeHtml(p.name || '') + '</h3>' +
              '<address class="b2b-map-list-item__address">' + addrHtml + '</address>' +
              (p.phone ? '<p class="b2b-map-list-item__phone"><a href="tel:' + escapeHtml(p.phone) + '">' + escapeHtml(p.phone) + '</a></p>' : '') +
              (p.website ? '<p class="b2b-map-list-item__website"><a href="' + escapeHtml(p.website) + '" target="_blank" rel="noopener noreferrer">Visit Website</a></p>' : '');
            fragment.appendChild(article);
          });
          listEl.innerHTML = '';
          listEl.appendChild(fragment);
        }
        rebuildSidebar();

        if (bounds && !bounds.isEmpty()) {
          map.fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            maxZoom: 12,
            duration: 1000
          });
        }

        map.addSource('b2b-locations', {
          type: 'geojson',
          data: geojson,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
          generateId: true
        });

        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'b2b-locations',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': ['step', ['get', 'point_count'], '#3a7d6e', 10, '#A4D2C5', 30, '#2d5f54'],
            'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
          }
        });

        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'b2b-locations',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12
          },
          paint: {
            'text-color': '#fff'
          }
        });

        map.addLayer({
          id: 'unclustered-point',
          type: 'circle',
          source: 'b2b-locations',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': '#3a7d6e',
            'circle-radius': 10,
            'circle-stroke-width': 3,
            'circle-stroke-color': '#fff'
          }
        });

        function buildPopupHTML(props) {
          var html = '<div class="b2b-map-popup">';
          if (props.name) html += '<strong>' + escapeHtml(props.name) + '</strong><br>';
          var addr = [props.address, props.city, props.province, props.zip, props.country].filter(Boolean).join(', ');
          if (addr) html += '<span class="b2b-map-popup__address">' + escapeHtml(addr) + '</span><br>';
          if (props.phone) html += '<a href="tel:' + escapeHtml(props.phone) + '">' + escapeHtml(props.phone) + '</a><br>';
          if (props.website) html += '<a href="' + escapeHtml(props.website) + '" target="_blank" rel="noopener">Visit Website</a>';
          html += '</div>';
          return html;
        }

        function highlightSidebarItem(lat, lng, listEl) {
          if (!listEl) return;
          var items = listEl.querySelectorAll('.b2b-map-list-item');
          items.forEach(function(item) {
            var itemLat = parseFloat(item.getAttribute('data-lat'));
            var itemLng = parseFloat(item.getAttribute('data-lng'));
            if (Math.abs(itemLat - lat) < 0.0001 && Math.abs(itemLng - lng) < 0.0001) {
              items.forEach(function(i) { i.classList.remove('b2b-map-list-item--active'); });
              item.classList.add('b2b-map-list-item--active');
              item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          });
        }

        map.on('click', 'clusters', function(e) {
          var features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
          if (!features.length) return;
          var clusterId = features[0].properties.cluster_id;
          map.getSource('b2b-locations').getClusterExpansionZoom(clusterId, function(err, zoom) {
            if (err) return;
            map.flyTo({
              center: features[0].geometry.coordinates,
              zoom: zoom,
              essential: true,
              speed: 1.2
            });
          });
        });

        map.on('click', 'unclustered-point', function(e) {
          var coords = e.features[0].geometry.coordinates.slice();
          var props = e.features[0].properties || {};

          while (Math.abs(e.lngLat.lng - coords[0]) > 180) {
            coords[0] += e.lngLat.lng > coords[0] ? 360 : -360;
          }

          popup.setLngLat(coords).setHTML(buildPopupHTML(props)).addTo(map);

          if (config.listId) {
            var listEl = document.getElementById(config.listId);
            highlightSidebarItem(coords[1], coords[0], listEl);
          }
        });

        map.on('mouseenter', 'clusters', function() {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'clusters', function() {
          map.getCanvas().style.cursor = '';
        });
        map.on('mouseenter', 'unclustered-point', function() {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'unclustered-point', function() {
          map.getCanvas().style.cursor = '';
        });

        if (config.listId) {
          var listEl = document.getElementById(config.listId);
          if (listEl) {
            listEl.addEventListener('click', function(e) {
              if (e.target.tagName === 'A') return;
              var item = e.target.closest('.b2b-map-list-item');
              if (!item) return;

              var lat = parseFloat(item.getAttribute('data-lat'));
              var lng = parseFloat(item.getAttribute('data-lng'));
              if (isNaN(lat) || isNaN(lng)) return;

              var items = listEl.querySelectorAll('.b2b-map-list-item');
              items.forEach(function(i) { i.classList.remove('b2b-map-list-item--active'); });
              item.classList.add('b2b-map-list-item--active');

              map.flyTo({
                center: [lng, lat],
                zoom: 14,
                essential: true,
                speed: 1.2
              });

              var matchingFeature = geojson.features.find(function(f) {
                return f.geometry &&
                       Math.abs(f.geometry.coordinates[0] - lng) < 0.0001 &&
                       Math.abs(f.geometry.coordinates[1] - lat) < 0.0001;
              });
              if (matchingFeature) {
                setTimeout(function() {
                  popup.setLngLat([lng, lat])
                       .setHTML(buildPopupHTML(matchingFeature.properties))
                       .addTo(map);
                }, 800);
              }
            });
          }
        }

        var mapSource = map.getSource('b2b-locations');
        var fetchedPages = { 1: true };
        var MAX_PAGES = 10;
        
        function fetchNextPages(pageNum) {
          if (!hasMorePages) return;
          if (pageNum > MAX_PAGES) return;
          if (fetchedPages[pageNum]) return;
          fetchedPages[pageNum] = true;
          
          fetchLocationsPage(locationsUrl, pageNum, pageParam).then(function(data) {
            if (data.features && data.features.length) {
              var existingCoords = {};
              geojson.features.forEach(function(f) {
                if (f.geometry && f.geometry.coordinates) {
                  var key = f.geometry.coordinates[0] + ',' + f.geometry.coordinates[1];
                  existingCoords[key] = true;
                }
              });
              var newFeatures = data.features.filter(function(f) {
                if (!f.geometry || !f.geometry.coordinates) return false;
                var key = f.geometry.coordinates[0] + ',' + f.geometry.coordinates[1];
                return !existingCoords[key];
              });
              if (newFeatures.length > 0) {
                geojson.features = geojson.features.concat(newFeatures);
                if (mapSource) mapSource.setData(geojson);
                rebuildSidebar();
              }
            }
            var nextPage = data.paginate && data.paginate.page ? data.paginate.page + 1 : pageNum + 1;
            hasMorePages = data.paginate && data.paginate.has_next === true;
            if (hasMorePages && nextPage <= MAX_PAGES && !fetchedPages[nextPage]) {
              setTimeout(function() { fetchNextPages(nextPage); }, 100);
            }
          }).catch(function(err) {
            console.warn('B2B Map: Background fetch error', err);
          });
        }
        
        if (hasMorePages) {
          fetchNextPages(2);
        }
        } catch (e) {
          showError('Error initializing map: ' + (e.message || e));
        }
      });
    }).catch(function(err) {
      showError(err.message || 'Unable to load locations. Please refresh the page.');
    });
  }

  var escapeDiv = null;
  function escapeHtml(str) {
    if (!str) return '';
    if (!escapeDiv) escapeDiv = document.createElement('div');
    escapeDiv.textContent = str;
    return escapeDiv.innerHTML;
  }

  function init() {
    var configs = window.B2BMapConfig;
    if (!configs) return;

    Object.keys(configs).forEach(function(sectionId) {
      initMap(configs[sectionId]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
